import React, { Component } from 'react';
import { connect } from 'react-redux';
import type { RequestStates } from 'types/redux';

import { Collapse } from 'antd';
import _ from 'lodash';
import moment from 'moment';

import { searchCampaignHistory } from 'actions/newTridentExtra';
import CDatePicker from 'components/custom/CDatePicker';
import { historyStatusOptions } from 'data/trident-campaigns';
import { nodeTypes } from 'data/trident-types';
import { Button, Form, Grid, Message } from 'semantic-grab-ui/src';
import type { TridentCampaignDetails } from 'types/trident-campaigns';
import HistoryRecordItem from './HistoryRecordItem';


const { Panel } = Collapse;

interface Props {
  model: TridentCampaignDetails;
  currentNode: string;
  parentKeyList: string[];
  searchCampaignHistory: (req: any) => void;
  searchRequest: RequestStates;
  campaignHistoryRecords: any[];
  initialUrlParams?: object;
}

const SearchStatus = {
  INIT: 0, // not searched yet
  LOADING: 1,
  SUCCESS: 2,
  FAILED: 3,
};

const KIBANA_STG_URL = "https://kibana.stg-myteksi.com/app/discoverLegacy#/?_a=(columns:!(app_name,message,msg),discover:(columns:!(message),interval:auto,sort:!()),filters:!(),index:grab,interval:auto,metadata:(indexPattern:grab,view:discover),query:(language:kuery,query:''),sort:!())&_q=(filters:!(('$state':(store:globalState),meta:(alias:!n,disabled:!f,index:grab,key:app_name,negate:!f,params:(query:reward-targeting),type:phrase),query:(match_phrase:(app_name:reward-targeting)))),query:(language:kuery,query:''))&_g=(filters:!(('$state':(store:globalState),meta:(alias:!n,disabled:!f,index:grab,key:app_name,negate:!f,params:(query:reward-targeting),type:phrase),query:(match_phrase:(app_name:reward-targeting))),('$state':(store:globalState),meta:(alias:!n,disabled:!f,index:grab,key:additional_data,negate:!f,params:(query:tm%3D167433),type:phrase),query:(match_phrase:(additional_data:tm%3D167433))),('$state':(store:globalState),meta:(alias:!n,disabled:!f,index:grab,key:additional_data,negate:!f,params:(query:'account%3Dpax:105916408'),type:phrase),query:(match_phrase:(additional_data:'account%3Dpax:105916408')))),refreshInterval:(pause:!t,value:300000),time:(from:now-5d,to:now))";
const TROUBLESHOOTING_WIKI_URL = 'https://wiki.grab.com/display/RTC/How+to+search+Trident+logs';

interface State {
  timeRangeStart: moment.Moment;
  timeRangeEnd: moment.Moment;
  accountID: string;
  subScenarioIndex: number;
  status: string;
  limit: number;
  searchStatus: number;
  historyRecords: any[];
  lastSearchWithAccountID: boolean;
}

// HistorySearchForm is the form for searching event processing history
class HistorySearchForm extends Component<Props, State> {

  constructor(props: Props) {
    super(props);
    let defaultTimeRangeEnd = moment().startOf('hour');
    const campaignEndTime = _.get(this.props.model, 'endTime', null);
    if (campaignEndTime != null && defaultTimeRangeEnd > campaignEndTime) {
      defaultTimeRangeEnd = campaignEndTime;
    }

    // Check for initial URL parameters
    const urlParams = _.get(this.props, 'constraints.initialUrlParams', {});
    const initialAccountID = _.get(urlParams, 'accountID', '');

    // Parse start and end times from URL params if provided
    let initialStartTime = moment(defaultTimeRangeEnd).subtract(1, 'days');
    let initialEndTime = defaultTimeRangeEnd;

    if (_.get(urlParams, 'timeRangeStart')) {
      const parsedStart = moment(_.get(urlParams, 'timeRangeStart'));
      if (parsedStart.isValid()) {
        initialStartTime = parsedStart;
      }
    }

    if (_.get(urlParams, 'timeRangeEnd')) {
      const parsedEnd = moment(_.get(urlParams, 'timeRangeEnd'));
      if (parsedEnd.isValid()) {
        initialEndTime = parsedEnd;
      }
    }

    this.state = {
      // search parameters
      timeRangeStart: initialStartTime,
      timeRangeEnd: initialEndTime,
      accountID: initialAccountID,
      subScenarioIndex: 0,
      status: '',
      limit: 5,

      // for display purpose
      searchStatus: SearchStatus.INIT,
      historyRecords: [],
      lastSearchWithAccountID: false,
    };
  }

  componentWillReceiveProps(nextProps: Props) {
    const reqState = _.get(nextProps, 'searchRequest');
    if (_.get(reqState, 'isFailure', false)) {
      this.setState({
        searchStatus: SearchStatus.FAILED,
      });
    } else if (_.get(reqState, 'isSuccessful', false)) {
      this.setState({
        searchStatus: SearchStatus.SUCCESS,
        historyRecords: _.get(nextProps, 'campaignHistoryRecords', []),
      });
    }
  }

  convertMomentToTimeSlot = (m: moment.Moment, roundUp: boolean) => {
    const copied = moment(m);
    if (roundUp && copied.minutes() > 0) {
      copied.add(1, 'hours');
    }
    return {
      year: copied.year(),
      month: copied.month() + 1,
      day: copied.date(),
      hour: copied.hour(),
    };
  };

  // getTimeZoneStr gets timezone info from a moment, and converts to a string representation
  // e.g. SGT will be converted to "8"
  getTimeZoneStr = (m: moment.Moment) => {
    const minOffset = m.utcOffset();
    const hourOffset = Math.floor(minOffset / 60);
    const minRemainder = minOffset % 60;
    if (minRemainder === 0) {
      return `${hourOffset}`;
    }
    return `${hourOffset}:${minRemainder}`;
  };

  searchHistory = () => {
    let tgID = 0;
    let tmID = 0;
    const currentNodeInfo = _.get(this.props.model, `nodes[${this.props.currentNode}]`, {});
    this.updateUrlWithSearchParams();
    if (currentNodeInfo.type === nodeTypes.scenarios) {
      // obtain treatment group ID to search with scenario nodes
      const foundTgID = _.get(this.props.model, `rootNodeTmGroupMapping[${this.props.currentNode}]`, 0);
      if (foundTgID === 0) {
        alert('No treatment group found for this node, please contact Trident support');
        return;
      }
      tgID = foundTgID;
    } else {
      // obtain treatment ID to search
      const tmIDs = _.get(this.props.model, `nodeTreatmentMapping[${this.props.currentNode}]`, []);
      // tgID means search history by treatment group ID
      if (_.isEmpty(tmIDs) && tgID < 1) {
        alert('No treatments found for this node, please contact Trident support');
        return;
      }
      tmID = tmIDs[0];
      if (tmIDs.length > 1) {
        // the root node is a chained scenario, in this case we need to check the root node's tm mapping to find the correct treatment
        const rootNodeID = this.props.parentKeyList[0];
        const ids = _.get(this.props.model, `nodeTreatmentMapping["${rootNodeID}-${this.state.subScenarioIndex + 1}"]`, []);
        if (_.isEmpty(ids)) {
          alert(`No treatment found for sub scenario ${this.state.subScenarioIndex + 1}, please contact Trident support`);
          return;
        }
        tmID = ids[0];
      }
    }

    // construct search request
    const req = {
      limit: this.state.limit,
      treatmentID: tmID,
      accountType: _.get(this.props.model, 'targetUserType', 'passenger'),
      accountID: _.trim(this.state.accountID),
      timeRangeStart: this.convertMomentToTimeSlot(this.state.timeRangeStart, false),
      timeRangeEnd: this.convertMomentToTimeSlot(this.state.timeRangeEnd, true),
      timeZone: this.getTimeZoneStr(moment()),
      status: this.state.status,
      treatmentGroupID: tgID,
    };

    this.props.searchCampaignHistory(req);

    this.setState({
      searchStatus: SearchStatus.LOADING,
      lastSearchWithAccountID: _.isEmpty(_.trim(this.state.accountID)),
      historyRecords: [],
    });
  };

  updateUrlWithSearchParams = () => {
    try {
      const url = new URL(window.location.href);
      const urlParams = url.searchParams;

      // Update URL parameters with current form values
      urlParams.set('f', 'history-search');

      // Add account ID if provided
      const accountID = _.trim(this.state.accountID);
      if (accountID) {
        urlParams.set('accountid', accountID);
      } else {
        urlParams.delete('accountid');
      }

      // Add start time if provided
      if (this.state.timeRangeStart) {
        urlParams.set('start', this.state.timeRangeStart.toISOString());
      } else {
        urlParams.delete('start');
      }

      // Add end time if provided
      if (this.state.timeRangeEnd) {
        urlParams.set('end', this.state.timeRangeEnd.toISOString());
      } else {
        urlParams.delete('end');
      }

      // Update the URL without refreshing the page
      const newUrl = url.toString();
      window.history.replaceState(null, '', newUrl);
    } catch (error) {
      // Silently fail if URL update fails - don't break the search functionality
      console.warn('Failed to update URL with search parameters:', error);
    }
  };

  handleInputChange = (event: any, data: any) => {
    const name = _.get(data, 'name');
    const value = _.get(data, 'value');
    this.setState({
      [name]: value,
    });
  };

  render() {
    // if this node is under a chained scenario, it will have multiple treatments mapping to it
    // then user needs to choose which treatment to search
    const tmIDs = _.get(this.props.model, `nodeTreatmentMapping[${this.props.currentNode}]`, []);
    const subScenarioOptions = tmIDs.map((_, index) => {
      return { key: index, value: index, text: `Scenario ${index + 1}` };
    });

    const userType = _.get(this.props.model, 'targetUserType', 'passenger');
    const startTime = _.get(this.props.model, 'startTime', moment().subtract(1, 'days'));
    const endTime = _.get(this.props.model, 'endTime', moment());
    const limitOptions = [
      { key: 5, value: 5, text: '5' },
      { key: 10, value: 10, text: '10' },
      { key: 20, value: 20, text: '20' },
      { key: 50, value: 50, text: '50' },
    ];

    const getHistoryRecordItems = () => {
      if (this.state.searchStatus !== SearchStatus.SUCCESS) {
        return;
      }

      if (!_.isEmpty(this.state.historyRecords)) {
        const currentNodeInfo = _.get(this.props.model, `nodes[${this.props.currentNode}]`, {});

        if (currentNodeInfo.type === nodeTypes.scenarios) {
          const tmIdToNodes = _.groupBy(this.state.historyRecords, 'ruleEvalRecord.treatmentID');

          return _.map(tmIdToNodes, (records, treatmentID) => (
            <Collapse style={{ marginTop: '10px' }} expandIconPosition="left" key={treatmentID}>
              <Panel header={`tm-${treatmentID}`}>
                {_.map(records, (nodeHistory) => (
                  <HistoryRecordItem item={nodeHistory} showAccountID={this.state.lastSearchWithAccountID} />
                ))}
              </Panel>
            </Collapse>
          ));
        }

        return _.map(this.state.historyRecords, (nodeHistory) => (
          <HistoryRecordItem item={nodeHistory} showAccountID={this.state.lastSearchWithAccountID} />
        ));
      }

      return (
        <Message>
          <p>No records found</p>
        </Message>
      );
    };


    // Presto data is not available on staging./wapp
    const isStaging = window.Grab.config.OMEGA_RTC_URI === 'https://omega-rtc.stg-myteksi.com';
    if (isStaging) {
      return (<div>
        <p>This feature is unavailable on staging <br />
          Check the <a href={KIBANA_STG_URL} target={'_blank'}> Kibana log  </a> and replace accountID (paxID) and treatmentID (hover over the node to find it) in the filter. <br />
          For more details, refer to this  <a href={TROUBLESHOOTING_WIKI_URL} target={'_blank'}>troubleshooting wiki page </a>
        </p>
      </div>);
    }

    return (
      <div>
        <Form>
          <Grid>
            <Grid.Row columns="equal">
              <Grid.Column>
                <Form.Input
                  name="accountID"
                  label={`${userType} ID`}
                  value={_.get(this.state, 'accountID')}
                  onChange={this.handleInputChange}
                />
              </Grid.Column>
              <Grid.Column>
                <CDatePicker
                  required
                  selectsStart
                  showTimeSelect
                  timeIntervals={60}
                  name="timeRangeStart"
                  label="Time Range Start"
                  selected={_.get(this.state, 'timeRangeStart')}
                  minDate={startTime}
                  maxDate={endTime}
                  handleInputChange={this.handleInputChange}
                />
              </Grid.Column>
              <Grid.Column>
                <CDatePicker
                  required
                  selectsStart
                  showTimeSelect
                  timeIntervals={60}
                  name="timeRangeEnd"
                  label="Time Range End"
                  selected={_.get(this.state, 'timeRangeEnd')}
                  minDate={_.get(this.state, 'timeRangeStart')}
                  maxDate={endTime}
                  handleInputChange={this.handleInputChange}
                />
              </Grid.Column>
            </Grid.Row>
            <Grid.Row columns="equal">
              <Grid.Column>
                <Form.Select
                  label="Status"
                  name="status"
                  options={historyStatusOptions}
                  value={_.get(this.state, 'status')}
                  onChange={this.handleInputChange}
                />
              </Grid.Column>

              <Grid.Column>
                <Form.Select
                  required
                  label="Display Limit"
                  name="limit"
                  options={limitOptions}
                  value={_.get(this.state, 'limit')}
                  onChange={this.handleInputChange}
                />
              </Grid.Column>
              {
                subScenarioOptions.length > 1 &&
                <Grid.Column>
                  <Form.Select
                    required
                    label="Sub Scenario"
                    name="subScenarioIndex"
                    options={subScenarioOptions}
                    value={_.get(this.state, 'subScenarioIndex')}
                    onChange={this.handleInputChange}
                  />
                </Grid.Column>
              }
            </Grid.Row>
            <Grid.Row>
              <Grid.Column>
                ⓘ Please try to narrow down the search time range to avoid search timeout.
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column>
                <Button
                  positive
                  onClick={this.searchHistory}
                  loading={this.state.searchStatus === SearchStatus.LOADING}
                >
                  Search
                </Button>

                <Button
                  onClick={this.props.handleCloseModal}
                  disabled={this.state.searchStatus === SearchStatus.LOADING}
                >
                  Close
                </Button>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column>
                {
                  this.state.searchStatus === SearchStatus.FAILED &&
                  <Message negative>
                    <p>Oops, search failed :/ Please try again</p>
                  </Message>
                }
                {
                  getHistoryRecordItems()
                }
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Form>

      </div>
    );
  }
}

export function mapStateToProps(state: StoreState) {
  return {
    campaignHistoryRecords: _.get(state, 'newTridents.campaignHistory', []),
    searchRequest: _.get(state.requests, 'searchCampaignHistoryRequest', {}),
  };
}

export default connect(mapStateToProps, {
  searchCampaignHistory,
})(HistorySearchForm);
